import { useState, useEffect, useCallback } from 'react';
import {
    BarChart3, Download, Filter, Calendar,
    Search, ArrowUpRight, TrendingUp, ShoppingCart,
    FileText, Loader2, ChevronLeft, ChevronRight,
    Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { downloadInvoice } from '../../lib/invoiceUtils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminReports() {
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: 'all',
        start_date: '',
        end_date: '',
        page: 1,
        limit: 10
    });

    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (filters.status !== 'all') params.append('status', filters.status);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            params.append('page', filters.page);
            params.append('limit', filters.limit);

            const response = await fetch(`${API}/admin/reports/sales?${params.toString()}`, {
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setReports(data);
            } else {
                toast.error('Failed to fetch sales reports');
            }
        } catch (error) {
            console.error('Failed to fetch reports:', error);
            toast.error('An error occurred while fetching reports');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(price || 0);
    };

    const getImageUrl = (url) => {
        if (!url) return '/placeholder.png';
        if (url.startsWith('http')) return url;
        return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'bg-success/10 text-success';
            case 'shipped': return 'bg-primary/10 text-primary';
            case 'confirmed': return 'bg-warning/10 text-warning';
            case 'cancelled': return 'bg-destructive/10 text-destructive';
            case 'pending': return 'bg-muted text-muted-foreground';
            case 'paid': return 'bg-success/10 text-success';
            case 'failed': return 'bg-destructive/10 text-destructive';
            default: return 'bg-muted text-muted-foreground';
        }
    };

    const totals = reports?.report?.reduce((acc, order) => {
        acc.revenue += (order.total || order.total_amount || 0);
        acc.orders += 1;
        return acc;
    }, { revenue: 0, orders: 0 }) || { revenue: 0, orders: 0 };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl text-secondary">Sales Reports</h1>
                    <p className="text-muted-foreground font-body mt-1">
                        Track and analyze your store's sales performance
                    </p>
                </div>
                <Button variant="outline" className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export CSV
                </Button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl p-6 shadow-soft space-y-4">
                <div className="flex items-center gap-2 text-secondary font-heading mb-2">
                    <Filter className="w-4 h-4" />
                    <span>Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-body text-muted-foreground">Order Status</label>
                        <Select
                            value={filters.status}
                            onValueChange={(v) => setFilters(f => ({ ...f, status: v, page: 1 }))}
                        >
                            <SelectTrigger className="bg-surface border-none rounded-xl">
                                <SelectValue placeholder="All Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-body text-muted-foreground">Start Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                type="date"
                                className="pl-10 bg-surface border-none rounded-xl"
                                value={filters.start_date}
                                onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value, page: 1 }))}
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-body text-muted-foreground">End Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                type="date"
                                className="pl-10 bg-surface border-none rounded-xl"
                                value={filters.end_date}
                                onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value, page: 1 }))}
                            />
                        </div>
                    </div>

                    <div className="flex items-end">
                        <Button
                            variant="outline"
                            className="w-full h-10 rounded-xl"
                            onClick={() => setFilters({
                                status: 'all',
                                start_date: '',
                                end_date: '',
                                page: 1,
                                limit: 10
                            })}
                        >
                            Clear Filters
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-soft">
                    <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                    <p className="text-muted-foreground font-body">Loading sales data...</p>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-soft">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-success/10 flex items-center justify-center text-success">
                                    <TrendingUp className="w-6 h-6" />
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground font-body mb-1">Filtered Revenue</p>
                            <h3 className="text-2xl font-heading text-secondary">{formatPrice(totals.revenue)}</h3>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-soft">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                                    <ShoppingCart className="w-6 h-6" />
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground font-body mb-1">Filtered Orders</p>
                            <h3 className="text-2xl font-heading text-secondary">{totals.orders}</h3>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-soft">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-secondary/10 flex items-center justify-center text-secondary">
                                    <FileText className="w-6 h-6" />
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground font-body mb-1">Avg. Order Value</p>
                            <h3 className="text-2xl font-heading text-secondary">
                                {formatPrice(totals.orders > 0 ? totals.revenue / totals.orders : 0)}
                            </h3>
                        </div>
                    </div>

                    {/* Sales Table */}
                    <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-surface/50 border-b border-gray-100">
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Order ID</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Customer</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Items</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Payment</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Amount</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Status</th>
                                        <th className="text-left py-4 px-6 font-body font-medium text-muted-foreground text-sm">Date</th>
                                        <th className="text-right py-4 px-6 font-body font-medium text-muted-foreground text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {reports?.report?.map((order) => (
                                        <tr key={order.order_id} className="hover:bg-surface/30 transition-colors">
                                            <td className="py-4 px-6 font-body font-medium text-primary">
                                                #{order.order_id}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex flex-col">
                                                    <span className="font-body text-sm text-secondary">{order.user?.name || 'N/A'}</span>
                                                    <span className="text-xs text-muted-foreground">{order.user?.email || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex -space-x-2">
                                                    {order.items?.map((item, idx) => (
                                                        <img
                                                            key={idx}
                                                            src={getImageUrl(item.image)}
                                                            alt={item.name}
                                                            className="w-8 h-8 rounded-lg border-2 border-white object-cover"
                                                            title={item.name}
                                                        />
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex flex-col gap-1">
                                                    <Badge className={`${getStatusColor(order.payment_status)} border-none capitalize px-2 py-0.5 rounded-md text-[10px]`}>
                                                        {order.payment_status || 'N/A'}
                                                    </Badge>
                                                    <span className="text-[10px] text-muted-foreground uppercase font-medium ml-1">
                                                        {order.payment_method || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 font-body font-medium text-secondary">
                                                {formatPrice(order.total_amount)}
                                            </td>
                                            <td className="py-4 px-6 font-body text-sm text-muted-foreground">
                                                <Badge className={`${getStatusColor(order.status)} border-none capitalize px-3 py-1 rounded-full text-xs`}>
                                                    {order.status}
                                                </Badge>
                                            </td>
                                            <td className="py-4 px-6 font-body text-sm text-muted-foreground">
                                                {formatDate(order.created_at)}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex justify-end gap-2">
                                                    <Link
                                                        to={`/admin/orders/${order.order_id}`}
                                                        className="p-2 hover:bg-surface rounded-lg transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-4 h-4 text-muted-foreground" />
                                                    </Link>
                                                    <button
                                                        onClick={() => downloadInvoice(order)}
                                                        className="p-2 hover:bg-surface rounded-lg transition-colors"
                                                        title="Download Invoice"
                                                    >
                                                        <Download className="w-4 h-4 text-muted-foreground" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!reports?.report || reports.report.length === 0) && (
                                        <tr className="bg-white">
                                            <td colSpan="8" className="py-20 text-center">
                                                <p className="text-muted-foreground font-body">No sales data found for the selected filters</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {reports?.report?.length > 0 && (
                                    <tfoot className="bg-surface/10 border-t border-gray-100 font-heading">
                                        <tr>
                                            <td colSpan="4" className="py-4 px-6 text-right text-secondary">Total (Filtered)</td>
                                            <td className="py-4 px-6 text-secondary">{formatPrice(totals.revenue)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                            <p className="text-sm text-muted-foreground font-body">
                                Showing {reports?.report?.length || 0} of {reports?.total || 0} orders (Page {filters.page} of {reports?.pages || 1})
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={filters.page === 1 || loading}
                                    onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!reports || filters.page >= reports.pages || loading}
                                    onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
